// path: frontend/src/components/Modals/ReassignApprovalModal.jsx
/**
 * Модалка переназначения при согласовании. Кандидаты приходят с
 * GET /assignment-approvals/candidates — сервер считает через
 * getApproverCandidates(actor, db) с effective-ролями. Источник правды —
 * бэкенд; раньше список собирался на клиенте — удалено.
 *
 * Фильтр «не текущий assignee» считается inline (без useMemo):
 * список мал, а React Compiler иначе не может сохранить ручную
 * мемоизацию — inferred deps `request` против указанных
 * `request?.assigneeId`.
 */
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModalShell } from "../ModalShell";
import { useNestedModalEscape } from "../Templates/useNestedModalEscape";
import { ASSIGNMENT_TARGET_KINDS } from "../../utils/assignmentApproval";
import { api } from "../../api/client";

const EMPTY_LIST = Object.freeze([]);

export function ReassignApprovalModal({ request, onConfirm, onClose }) {
  useNestedModalEscape(onClose);

  const { data: candidatesData } = useQuery({
    queryKey: ["assignment-approvals", "candidates"],
    queryFn: () => api.get("/assignment-approvals/candidates"),
    staleTime: 30_000,
  });

  const allCandidates = candidatesData ?? EMPTY_LIST;
  const currentAssigneeId = request?.assigneeId ?? null;
  const candidates = currentAssigneeId
    ? allCandidates.filter((e) => e.id !== currentAssigneeId)
    : allCandidates;

  const [selectedId, setSelectedId] = useState(() => candidates[0]?.id || "");

  const kindLabel =
    ASSIGNMENT_TARGET_KINDS[request?.targetType]?.label || "Объект";

  const handleSubmit = useCallback(() => {
    if (!selectedId) return;
    onConfirm(selectedId);
  }, [selectedId, onConfirm]);

  const footer = (
    <div className="modal-foot">
      <div className="spacer" />
      <button type="button" className="btn ghost" onClick={onClose}>
        Отмена
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSubmit}
        disabled={!selectedId}
      >
        Переназначить
      </button>
    </div>
  );

  return (
    <ModalShell
      title={`Переназначить ${kindLabel.toLowerCase()}`}
      onClose={onClose}
      width={520}
      footer={footer}
      showSave={false}
    >
      <p className="mut sm mb-2">
        Выберите сотрудника из вашего подразделения - он заменит исходного
        исполнителя. Автор запроса получит уведомление о решении.
      </p>

      {candidates.length === 0 ? (
        <div className="info-box">
          В вашем подразделении нет других сотрудников - переназначение
          невозможно. Согласуйте или отклоните запрос.
        </div>
      ) : (
        <div className="roles-list">
          {candidates.map((e) => (
            <label key={e.id} className="roles-item">
              <input
                type="radio"
                name="reassign-employee"
                checked={selectedId === e.id}
                onChange={() => setSelectedId(e.id)}
              />
              <span className="flex-1">
                <b>
                  {e.last} {e.first}
                </b>
              </span>
              <span className="mut sm">{e.position || ""}</span>
            </label>
          ))}
        </div>
      )}
    </ModalShell>
  );
}
